import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  BRAND_OPTIONS,
  PRODUCT_FORM_FIELDS,
  TV_BRAND_OPTIONS,
  getProductCategoryOptions,
} from "../../../Constant/AllFormFields";

// ── Searchable select component ──────────────────────────────────────
const SearchableSelect = ({ value, options, placeholder, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase()),
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt);
    setOpen(false);
    setSearch("");
  };

  const handleOpen = () => {
    setOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <>
      <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
        {/* Trigger — looks like a native select */}
        <div
          className={classNames("field", "searchable-select-trigger", {
            invalid: !!error,
          })}
          onClick={handleOpen}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            userSelect: "none",
            color: value ? "inherit" : "#9ca3af",
          }}
        >
          <span>{value || placeholder}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              flexShrink: 0,
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Dropdown */}
        {open && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              zIndex: 1000,
              overflow: "hidden",
            }}
          >
            {/* Search input */}
            <div style={{ padding: "8px" }}>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "0.875rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Options list */}
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: "0 0 6px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {filtered.length === 0 ? (
                <li
                  style={{
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                    color: "#9ca3af",
                  }}
                >
                  No results found
                </li>
              ) : (
                filtered.map((opt) => (
                  <li
                    key={opt}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding: "8px 12px",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      background: opt === value ? "#eff6ff" : "transparent",
                      color: opt === value ? "#2447AD" : "inherit",
                      fontWeight: opt === value ? 600 : 400,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        opt === value ? "#eff6ff" : "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        opt === value ? "#eff6ff" : "transparent")
                    }
                  >
                    {opt}
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

const ProductDetailsSection = ({
  values,
  errors,
  handleFieldChange,
  handleFieldBlur,
  planCategory, // category from selected plan card (e.g. "TV", "Appliances")
}) => {
  // Filtered product category options based on selected plan category
  const categoryOptions = getProductCategoryOptions(planCategory);
  // console.log("Category", categoryOptions);

  const isTVCategory =
    categoryOptions?.includes("Television") || categoryOptions?.includes("TV");

  const brandOptions = isTVCategory ? TV_BRAND_OPTIONS : BRAND_OPTIONS;
  // TODO: don't remove to this commented code
  // console.log("TV brands : ", tvBrands);
  // When the plan category changes, reset productCategory if the current value
  // is not in the new filtered list
  // useEffect(() => {
  //   if (
  //     planCategory &&
  //     values.productCategory &&
  //     !categoryOptions.includes(values.productCategory)
  //   ) {
  //     handleFieldChange("productCategory", categoryOptions[0] || "");
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [planCategory]);

  useEffect(() => {
    const isTVCategory =
      categoryOptions?.includes("Television") ||
      categoryOptions?.includes("TV");

    if (isTVCategory) {
      // TV plan — auto-select Television
      const tvCategory = categoryOptions.includes("Television")
        ? "Television"
        : "TV";
      handleFieldChange("productCategory", tvCategory);
    } else {
      // Any other plan switch — reset product category to blank
      handleFieldChange("productCategory", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCategory]);

  // in words
  const numberToWords = (num) => {
    if (!num || Number(num) === 0) return "";

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const convertLessThanThousand = (number) => {
      let result = "";

      if (number >= 100) {
        result += ones[Math.floor(number / 100)] + " Hundred ";
        number %= 100;
      }

      if (number >= 20) {
        result += tens[Math.floor(number / 10)] + " ";
        number %= 10;
      }

      if (number > 0) {
        result += ones[number] + " ";
      }

      return result.trim();
    };

    let number = Number(num);
    let result = "";

    if (number >= 10000000) {
      result +=
        convertLessThanThousand(Math.floor(number / 10000000)) + " Crore ";
      number %= 10000000;
    }

    if (number >= 100000) {
      result += convertLessThanThousand(Math.floor(number / 100000)) + " Lakh ";
      number %= 100000;
    }

    if (number >= 1000) {
      result +=
        convertLessThanThousand(Math.floor(number / 1000)) + " Thousand ";
      number %= 1000;
    }

    if (number > 0) {
      result += convertLessThanThousand(number);
    }

    return `${result.trim()} Rupees Only`;
  };
  return (
    <section className="card">
      <h2 className="card__title">
        <span className="section-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 7h18M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M3 7l2-4h14l2 4"
            />
          </svg>
        </span>
        Product Details
      </h2>

      <div className="grid-2">
        {/* Product Category — filtered by plan category */}
        <div className="field-wrap">
          <span className="field-label">Product Category</span>

          <SearchableSelect
            value={values.productCategory}
            options={categoryOptions}
            placeholder="Please Select Category"
            onChange={(val) => handleFieldChange("productCategory", val)}
            error={errors.productCategory}
          />
          <div className="err-msg">{errors.productCategory || ""}</div>
        </div>

        {/* Brand Name */}
        <div className="field-wrap">
          <span className="field-label">Brand Name</span>

          <SearchableSelect
            value={values.brandName}
            options={brandOptions}
            placeholder="Please Select Brand"
            onChange={(val) => handleFieldChange("brandName", val)}
            error={errors.brandName}
          />
          <div className="err-msg">{errors.brandName || ""}</div>
        </div>

        {/* {PRODUCT_FORM_FIELDS.map((field) => (
          <div className="field-wrap" key={field.id}>
            <span className="field-label">{field.label}</span>
            <input
              id={field.id}
              className={classNames("field", field.mono && "field--mono", {
                invalid: !!errors[field.id],
              })}
              type={field.type}
              inputMode={field.inputMode}
              maxLength={field.maxLength}
              placeholder={field.placeholder}
              required={field.required}
              value={values[field.id]}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onBlur={() => handleFieldBlur(field.id)}
            />
            <div className="err-msg">{errors[field.id] || ""}</div>
          </div>
        ))} */}
        {PRODUCT_FORM_FIELDS.map((field) => (
          <div className="field-wrap" key={field.id}>
            <span className="field-label">{field.label}</span>

            <div className="field-input-wrapper">
              {field.id === "invoiceValue" && (
                <span className="currency-symbol">₹</span>
              )}

              <input
                id={field.id}
                className={classNames(
                  "field",
                  field.mono && "field--mono",
                  field.id === "invoiceValue" && "field--currency",
                  {
                    invalid: !!errors[field.id],
                  },
                )}
                type={field.id === "invoiceValue" ? "text" : field.type}
                inputMode={
                  field.id === "invoiceValue" ? "numeric" : field.inputMode
                }
                maxLength={field.maxLength}
                placeholder={field.placeholder}
                required={field.required}
                value={values[field.id]}
                onChange={(e) => {
                  let value = e.target.value;

                  // Only numbers for Invoice Value
                  if (field.id === "invoiceValue") {
                    value = value.replace(/[^0-9]/g, "");
                  }

                  handleFieldChange(field.id, value);
                }}
                onBlur={() => handleFieldBlur(field.id)}
              />
            </div>
            {/* Amount in words */}
            {field.id === "invoiceValue" && values.invoiceValue && (
              <div className="amount-in-words">
                {" "}
                {numberToWords(values.invoiceValue)}
              </div>
            )}

            <div className="err-msg">{errors[field.id] || ""}</div>
          </div>
        ))}

        {/*  Purchase date  */}
        {/* Purchase Date */}
        <div className="field-wrap">
          <span className="field-label">Purchase Date</span>
          <input
            id="purchaseDate"
            className={classNames("field", {
              invalid: !!errors.purchaseDate,
            })}
            type="date"
            name="purchaseDate"
            value={values.purchaseDate}
            onChange={(e) => handleFieldChange("purchaseDate", e.target.value)}
            onBlur={() => handleFieldBlur("purchaseDate")}
          />
          <div className="err-msg">{errors.purchaseDate || ""}</div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetailsSection;
